import { AfterViewInit, Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Capacitor } from "@capacitor/core";
import { IonModal } from "@ionic/angular";
import { SavePassword } from "capacitor-ios-autofill-save-password";
import { AccountService } from "../../lib/account.service";
import { EMAIL_VALIDATOR } from "../../lib/email.validator";
import { Modal } from "../../lib/modal";
import { SessionDetails } from "../../lib/models/survey";
import { NavigationService } from "../../lib/navigation.service";
import { GoogleOauth } from "../../lib/oauth-plugin";
import { PasswordValidator } from "../../lib/password.validator";
import { FetchStatus, postApi, sleep } from "../../lib/util";
import { StatusBar } from "@capacitor/status-bar";

enum LoginModalState {
  // Choose between signing up and signing in
  Initial,

  // Registration
  NewUser,

  // Login, or enter forgot password
  ReturningUser,

  // Send an email to an address
  ForgotPassword,

  // Enter from having password_reset_token as a query parameter or an error on login
  ResetPassword,
}

type SessionStatus = {
  SessionExpires: string;
};

// Login Modal Flow:
// When the toolbar is loaded, it loads the login modal. In ngOnInit, it checks if the user is logged in.
// If the user is not logged in, the modal opens.
// If a request to the API fails with 401 Unauthorized, the modal is opened.
// The session attached to the modal automatically renews itself every few minutes, until it is not allowed to anymore.
// After logging in through the modal, the user is redirected home and loses whatever page state they previously had.
@Component({
  selector: "app-login-modal",
  templateUrl: "./login-modal.component.html",
  styleUrls: ["./login-modal.component.css"],
})
export class LoginModalComponent extends Modal<LoginModalState> implements OnInit, AfterViewInit {
  // Import enum for use in template
  readonly LoginModalState = LoginModalState;
  readonly validations = PasswordValidator.validations;

  account = inject(AccountService);
  nav = inject(NavigationService);

  // Disables all buttons while a request is in flight
  // Only cleared by acknowledging an error popup
  signinDisabled = false;

  regForm!: FormGroup;
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  resetPasswordForm!: FormGroup;

  resetPasswordToken?: string;

  static lastTestedServer = new Date(0);
  static serverTestToken = 0;
  static readonly SERVER_TEST_DURATION = new Date(0, 0, 0, 0, 0, 60, 0); // 60s

  isKeyboardOnscreen = false;

  ngOnInit() {
    this.regForm = new FormGroup({
      email: new FormControl("", EMAIL_VALIDATOR),
      password: new FormControl("", [PasswordValidator.check]),
      confirm_password: new FormControl("", [PasswordValidator.checkConfirm]),
      //terms: new FormControl(false, Validators.requiredTrue),
    });

    this.loginForm = new FormGroup({
      email: new FormControl("", EMAIL_VALIDATOR),
      password: new FormControl("", [Validators.required]),
    });

    this.forgotPasswordForm = new FormGroup({
      email: new FormControl("", EMAIL_VALIDATOR),
    });

    this.resetPasswordForm = new FormGroup({
      password: new FormControl("", [PasswordValidator.check]),
    });

    // Overlay toolbar with native status bar so they appear as one - only supported on Android.
    if (Capacitor.getPlatform() == "android") {
      StatusBar.setOverlaysWebView({ overlay: true });
    }
  }

  ngAfterViewInit() {
    if (this.account.pageRequiresAuth() && !this.account.isSignedIn()) {
      this.open();
    }
  }

  @ViewChild(IonModal) modal!: IonModal;
  getModal() {
    return this.modal;
  }
  getInitialState() {
    return LoginModalState.Initial;
  }

  override open() {
    if (!this.isOpen) {
      super.open();

      void this.testServerUp();
    }
  }

  async testServerUp() {
    const token = Math.random();
    LoginModalComponent.serverTestToken = token;

    await sleep(500);

    // Test the server to see if it is up every time the modal opens, unless it checked recently
    // Also checks if a request from another thread is currently in flight with this.serverTestToken
    if (
      LoginModalComponent.serverTestToken == token &&
      LoginModalComponent.lastTestedServer.getTime() > Date.now() + LoginModalComponent.SERVER_TEST_DURATION.getTime()
    ) {
      LoginModalComponent.lastTestedServer = new Date();
      await postApi<SessionStatus>(
        "/session/status",
        null,
        (_res: SessionStatus) => {
          // Logged in, but not much we can do about it
          console.error("Logged in, but opened login modal!");
        },
        (err: FetchStatus) => {
          // A code beside 401 Forbidden for a logged out user indicates the server is down/having problems
          // On a 401, the internal handler will delete all session state.
          if (err.code != 401) {
            void this.account.alert(
              "Server Issue",
              "Sorry, our service is currently unavailable. Please try again later."
            );
          }
        }
      );
    }
  }

  openPasswordReset() {
    this.open();
    this.state = LoginModalState.ResetPassword;
  }

  showNewUserMenu() {
    this.state = LoginModalState.NewUser;
  }

  showReturningUserMenu() {
    this.state = LoginModalState.ReturningUser;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  override async back() {
    switch (this.state) {
      case LoginModalState.Initial: {
        // Nowhere to go from here
        break;
      }
      case LoginModalState.NewUser: {
        this.state = LoginModalState.Initial;
        break;
      }
      case LoginModalState.ReturningUser: {
        this.state = LoginModalState.Initial;
        break;
      }
      case LoginModalState.ForgotPassword: {
        this.state = LoginModalState.ReturningUser;
        break;
      }
    }
  }

  settings() {
    NavigationService.forceTo("/settings");
    void this.close();
  }

  submitRegistration() {
    this.signinDisabled = true;

    const val = this.regForm.value as { email: string; password: string };

    this.account.register(
      String(val.email),
      String(val.password),
      this.regComplete.bind(this),
      this.regError.bind(this)
    );
  }

  async regError(err: FetchStatus) {
    let message = "";

    if (err.reason === "password-fails-requirements") {
      // Should be impossible since password is also validated client-side
      message = "Please use a different password.";
    } else if (err.reason === "account-exists") {
      message = "That email already has an account. Please try logging in, or using Google.";
    } else {
      message = "Sorry, your account could not be created. Please try again later.";
    }

    await this.account.alert("Registration Error", message);

    this.signinDisabled = false;
  }

  async regComplete() {
    await this.autosaveCredentials(this.regForm);

    await this.account.alert("Email Verification", `Thank you for
      signing up! We've sent a verification link to your
      email address. Please check your inbox (and spam folder, just in case)
      and click the link to confirm your account.`);

    NavigationService.reload();
  }

  submitLogin() {
    this.signinDisabled = true;

    const val = this.loginForm.value as { email: string; password: string };

    this.account.login(
      String(val.email),
      String(val.password),
      this.loginComplete.bind(this),
      this.loginError.bind(this)
    );
  }

  async loginError(err: FetchStatus) {
    let message = "";

    const resetPasswordToken = err.extra?.passwordResetToken as string | undefined;
    if (resetPasswordToken != null) {
      // Switch to password reset pane if our error was that it is needed
      this.resetPasswordToken = resetPasswordToken;
      this.state = LoginModalState.ResetPassword;
      this.signinDisabled = false;
      return;
    }

    if (err.reason === "account-locked") {
      message = "Your account has been locked. Please check your email to reset your password.";
    } else if (err.reason === "account-different-auth-method") {
      message = "You didn't use a username and password to login. Please exit and use another button.";
    } else if (err.reason == "email-not-verified") {
      message = "It looks like you haven’t verified your email yet. Please check your inbox (and spam folder) for a verification link to complete the process and log in.";
    } else if (err.reason == "email-not-verified-expired") {
      message = "It looks like you haven’t verified your email yet. A new verification link has been sent to your inbox. Please check your inbox (and spam folder) for a verification link to complete the process and log in.";
    } else if (err.code == 401) {
      // Other generic forbidden
      message = "Your email or password are incorrect.";
    } else {
      // Server or connection error
      message = "Failed to log in. Please try again later.";
    }
    await this.account.alert("Login Error", message);

    this.signinDisabled = false;
    await this.nav.to('/landing', true);
  }

  async autosaveCredentials(form: FormGroup | undefined) {
    if (Capacitor.getPlatform() === "ios") {
      const autofillEmail = (form?.value?.email ??
        this.account.email ??
        this.loginForm.value.email ??
        this.regForm.value.email ??
        this.forgotPasswordForm.value.email) as string | undefined;
      const autofillPassword = (form?.value?.password ??
        this.loginForm.value.password ??
        this.regForm.value.password ??
        this.resetPasswordForm.value.password) as string | undefined;

      if (!autofillEmail || !autofillPassword) {
        console.error("Unable to autosave password");
        return;
      }

      console.log("Trying to autosave", autofillEmail, autofillPassword);
      await SavePassword.promptDialog({
        username: autofillEmail,
        password: autofillPassword,
      });
    }
  }

  async loginComplete() {
    await this.autosaveCredentials(this.loginForm);

    // Deliberately destroy the page state since it is probably now broken by the logout
    NavigationService.reload();
  }

  submitForgotPassword() {
    this.signinDisabled = true;

    const val = this.forgotPasswordForm.value as { email: string };

    this.account.forgotPassword(
      String(val.email),
      async () => {
        await this.forgotPasswordComplete();
        this.state = LoginModalState.ReturningUser;
      },
      // Should never be possible to get this error
      this.loginError.bind(this)
    );
  }

  async forgotPasswordComplete() {
    await this.account.alert("Reset Password", "Please check your email. If it is a valid account, a link has been sent to your email to reset your password.");

    this.state = LoginModalState.ReturningUser;
    this.signinDisabled = false;

    this.account.goHome();
  }

  submitResetPassword() {
    this.signinDisabled = true;

    const val = this.resetPasswordForm.value as { password: string };

    this.account.resetPassword(
      this.resetPasswordToken!, // This is getting captured in landing.page.ts.
      String(val.password),
      this.resetPasswordComplete.bind(this),
      this.resetPasswordError.bind(this)
    );
  }

  async resetPasswordError(err: FetchStatus) {
    let message = "";

    if (err.reason === "password-fails-requirements") {
      message = "Please use a different password.";
    } else if (err.reason === "password-duplicate") {
      message = "Your new password can't be the same as your old password.";
    } else {
      // Server or connection error
      message = "Failed to reset your password. Please try again later.";
    }
    await this.account.alert("Login Error", message);

    this.signinDisabled = false;
  }

  async resetPasswordComplete(_res: SessionDetails) {
    this.signinDisabled = false;

    await this.account.alert("Password Reset", "Your password has successfully been reset. Please login with your new password.");

    this.account.goHome();
  }

  async openGoogleAuth() {
    this.signinDisabled = true;

    await GoogleOauth.doGoogleOauth(this.googleOauthSucceed.bind(this), this.googleOauthFail.bind(this));
  }

  async googleOauthSucceed(res: SessionDetails) {
    await this.account.updateSessionDetails(res);
    NavigationService.reload();

    this.signinDisabled = false;
  }

  async googleOauthFail(err: FetchStatus) {
    await this.account.deleteSessionDetails();

    let message = "";
    if (err.reason == "account-locked") {
      message = "Your account has been locked. Please check your email to reset your password.";
    } else if (err.reason == "account-different-auth-method") {
      message = "You didn't use Continue with Google to login. Please exit and use another button.";
    } else {
      // Server or connection error
      message = "Sorry, Continue with Google isn't working right now. Please try again later.";
    }
    await this.account.alert("Login Error", message);

    this.signinDisabled = false;
  }
}
